import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import RestoreFromTrashOutlinedIcon from "@mui/icons-material/RestoreFromTrashOutlined";
import KeyIcon from "@mui/icons-material/KeyOutlined";
import LoginIcon from "@mui/icons-material/Login";
import Groups2Icon from "@mui/icons-material/Groups2";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import CreateUserModal from "../admin/CreateUserModal";
import { useStatus } from "../../popup/NotificationContext";
import { loginSuccess } from "../../store/authSlice";
import {
  adminApi,
  useDeleteUserMutation,
  useEditUserMutation,
  useGetAllUsersQuery,
  useImpersonateUserMutation,
  useRevealUserPasswordMutation,
  useResetPasswordMutation,
  useRestoreUserMutation,
} from "../../redux/admin/adminApi";
import {
  canCreateRole,
  canManageRole,
  getRoleDisplayName,
  getUserActionsEffective,
  normalizeRole,
  ROLES,
} from "../../utils/rbacHelper";
import {
  CUSTOM_ROWS_PER_PAGE_OPTIONS,
  getDisplayRange,
  getPaginatedRows,
  getRowsPerPageLabel,
  getTotalPagesForRows,
  ROWS_PER_PAGE_MENU_PROPS,
} from "../../utils/tablePagination";

const ORG_ROLES = [
  ROLES.MANAGER,
  ROLES.HR,
  ROLES.OFFICE_EMPLOYEE,
  ROLES.FIELD_EMPLOYEE,
];

const ORG_HIDDEN_PERMISSION_KEYS = [
  "manageCredits",
  "requestCredits",
  "purchaseLicense",
  "viewMyTasks",
];

const buildCsv = (rows) => {
  const headers = ["Name", "Role", "User ID", "Email", "Mobile", "Created By", "Status"];
  const data = rows.map((row) =>
    [
      row.username,
      row.roleLabel,
      row.uniqueId,
      row.email,
      row.mobileNo,
      row.createdByName,
      row.disabled ? "Disabled" : "Active",
    ]
      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...data].join("\n");
};

const resolveCreatedByLabel = (user) => {
  const getLabel = (candidate) => {
    if (!candidate) return "";
    const username = String(candidate?.username || "").trim();
    const uniqueId = String(candidate?.uniqueId || "").trim();
    if (username && uniqueId) return `${username} (${uniqueId})`;
    return username || uniqueId || "";
  };

  return (
    String(user?.createdByName || "").trim() ||
    getLabel(user?.createdBy) ||
    getLabel(user?.dealerOwner) ||
    getLabel(user?.subAdminOwner) ||
    "-"
  );
};

const formatActivityTimestamp = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const formatPresenceStatus = (value) => (value ? "Online" : "Offline");

export default function EmployeePortalModal({ open = true, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const currentUserRole = normalizeRole(currentUser?.role);
  const userActions = getUserActionsEffective(currentUser);
  const { showStatus } = useStatus();
  const { data: usersPayload = [], refetch, isFetching } = useGetAllUsersQuery();
  const [deleteUser] = useDeleteUserMutation();
  const [restoreUser] = useRestoreUserMutation();
  const [impersonateUser] = useImpersonateUserMutation();
  const [editUser] = useEditUserMutation();
  const [revealUserPassword] = useRevealUserPasswordMutation();
  const [resetPassword, { isLoading: isResettingPassword }] = useResetPasswordMutation();

  const [searchName, setSearchName] = useState("");
  const [searchLoginId, setSearchLoginId] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [modalMode, setModalMode] = useState("table");
  const [editingUser, setEditingUser] = useState(null);
  const [resetDialogUser, setResetDialogUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [togglingUserId, setTogglingUserId] = useState("");
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [loadingPasswordIds, setLoadingPasswordIds] = useState(new Set());
  const createButtonRef = useRef(null);

  const creatableOrgRoles = useMemo(
    () => ORG_ROLES.filter((role) => canCreateRole(currentUserRole, role)),
    [currentUserRole]
  );

  const normalizedUsers = useMemo(
    () =>
      (Array.isArray(usersPayload) ? usersPayload : [])
        .map((user) => ({
          ...user,
          role: normalizeRole(user?.role),
        }))
        .filter((user) => ORG_ROLES.includes(user.role))
        .filter((user) => canManageRole(currentUserRole, user.role) || String(user?._id || "") === String(currentUser?._id || ""))
        .map((user) => ({
          ...user,
          disabled: user?.isActive === false,
          isOnline: Boolean(user?.isOnline),
          uniqueId: user?.uniqueId || "-",
          mobileNo: user?.mobileNo || "-",
          email: user?.email || "-",
          createdByName: resolveCreatedByLabel(user),
          roleLabel: getRoleDisplayName(user.role),
          lastLoginLabel: formatActivityTimestamp(user?.lastLoginAt),
          lastActivityLabel: formatActivityTimestamp(
            user?.lastActivityAt || user?.updatedAt || user?.createdAt
          ),
          lastSeenLabel: formatActivityTimestamp(user?.lastSeen),
        })),
    [currentUser?._id, currentUserRole, usersPayload]
  );

  const filteredUsers = useMemo(() => {
    const nameQuery = searchName.trim().toLowerCase();
    const loginQuery = searchLoginId.trim().toLowerCase();
    const phoneQuery = searchPhone.trim();

    return normalizedUsers.filter((user) => {
      if (roleFilter !== "all" && user.role !== roleFilter) return false;
      if (nameQuery && !String(user?.username || "").toLowerCase().includes(nameQuery)) return false;
      if (loginQuery && !String(user?.uniqueId || "").toLowerCase().includes(loginQuery)) return false;
      if (phoneQuery && !String(user?.mobileNo || "").includes(phoneQuery)) return false;
      return true;
    });
  }, [normalizedUsers, roleFilter, searchName, searchLoginId, searchPhone]);

  const totalPages = getTotalPagesForRows(filteredUsers.length, rowsPerPage);
  const pagedUsers = useMemo(
    () => getPaginatedRows(filteredUsers, page, rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );
  const displayRange = useMemo(
    () => getDisplayRange(filteredUsers.length, page, rowsPerPage),
    [filteredUsers.length, page, rowsPerPage]
  );

  useEffect(() => {
    if (page <= Math.max(totalPages, 1)) return;
    setPage(Math.max(totalPages, 1));
  }, [page, totalPages]);

  const resetFilters = () => {
    setSearchName("");
    setSearchLoginId("");
    setSearchPhone("");
    setRoleFilter("all");
    setPage(1);
  };

  const canRevealPasswords = Boolean(userActions.canResetPassword);

  const clearRevealedPasswords = () => {
    setRevealedPasswords({});
    setLoadingPasswordIds(new Set());
  };

  const handleCloseModal = () => {
    clearRevealedPasswords();
    setEditingUser(null);
    setModalMode("table");
    setResetDialogUser(null);
    setNewPassword("");
    setShowNewPassword(false);
    onClose?.();
  };

  const handleDownloadCsv = () => {
    const csv = buildCsv(filteredUsers);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "organization-staff.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showStatus("success", "Organization staff CSV downloaded.");
  };

  const closeForm = () => {
    setEditingUser(null);
    setModalMode("table");
  };

  const handleLoginAsUser = async (targetUserId) => {
    if (!userActions.canLoginAsUser) return;
    try {
      const currentAdminToken = localStorage.getItem("token");
      if (currentAdminToken) localStorage.setItem("adminFallbackToken", currentAdminToken);

      const response = await impersonateUser(targetUserId).unwrap();
      dispatch(adminApi.util.resetApiState());
      dispatch(loginSuccess({ user: response.user, token: response.token }));
      onClose?.();
      navigate("/dashboard", { replace: true, state: { __impersonate: true, t: Date.now() } });
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to login as user.");
    }
  };

  const handleTogglePassword = async (userId) => {
    const targetId = String(userId || "");
    if (!targetId || !canRevealPasswords) return;

    if (Object.prototype.hasOwnProperty.call(revealedPasswords, targetId)) {
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
      return;
    }

    setLoadingPasswordIds((prev) => new Set(prev).add(targetId));
    try {
      const response = await revealUserPassword(targetId).unwrap();
      setRevealedPasswords((prev) => ({
        ...prev,
        [targetId]: response?.password || "",
      }));
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to reveal password.");
    } finally {
      setLoadingPasswordIds((prev) => {
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
    }
  };

  const renderPasswordCell = (user) => {
    const userId = String(user?._id || "");
    const loading = loadingPasswordIds.has(userId);
    const revealedPassword = revealedPasswords[userId];
    const isVisible = Object.prototype.hasOwnProperty.call(revealedPasswords, userId);

    if (!canRevealPasswords) {
      return <VisibilityOffIcon sx={{ fontSize: 18, color: "#9ca3af" }} />;
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontWeight: 700, minWidth: 74 }}>
          {isVisible ? revealedPassword : "••••••••"}
        </Typography>
        <IconButton
          type="button"
          size="small"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleTogglePassword(userId);
          }}
          disabled={loading}
          sx={{ color: "#2563eb" }}
        >
          {loading ? (
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>...</Typography>
          ) : isVisible ? (
            <VisibilityIcon sx={{ fontSize: 18 }} />
          ) : (
            <VisibilityOffIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Stack>
    );
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId).unwrap();
      showStatus("success", "Organization user deleted.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to delete user.");
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      await restoreUser(userId).unwrap();
      showStatus("success", "Organization user restored.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to restore user.");
    }
  };

  const handleResetPassword = async () => {
    if (!resetDialogUser?._id || !newPassword.trim()) {
      showStatus("warning", "New password required.");
      return;
    }

    try {
      await resetPassword({ userId: resetDialogUser._id, password: newPassword }).unwrap();
      showStatus("success", "Password reset successful.");
      setResetDialogUser(null);
      setNewPassword("");
      setShowNewPassword(false);
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to reset password.");
    }
  };

  const handleToggleDisabled = async (user) => {
    if (!user?._id) return;
    if (String(user._id) === String(currentUser?._id)) {
      showStatus("warning", "You cannot disable your own account.");
      return;
    }

    try {
      setTogglingUserId(String(user._id));
      await editUser({
        userId: user._id,
        username: user.username || "",
        uniqueId: user.uniqueId === "-" ? "" : user.uniqueId,
        email: user.email === "-" ? "" : user.email,
        mobileNo: user.mobileNo === "-" ? "" : user.mobileNo,
        role: user.role,
        permissions: user.permissions || {},
        devicePermissions: user.devicePermissions || {},
        userPermissions: user.userPermissions || {},
        isActive: user.disabled,
      }).unwrap();
      showStatus("success", user.disabled ? "Account enabled successfully." : "Account disabled successfully.");
      refetch();
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to update account status.");
    } finally {
      setTogglingUserId("");
    }
  };

  const openCreate = () => {
    if (!creatableOrgRoles.length) {
      showStatus("warning", "You do not have permission to create organization staff.");
      return;
    }
    setEditingUser(null);
    setModalMode("create");
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "76vh" } }}>
        <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
          <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Groups2Icon sx={{ color: "#2563eb" }} />
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 800 }}>Organization Staff</Typography>
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                  Manage Manager, HR, Office Employee, and Field Employee accounts in one place.
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          <>
              <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #e5e7eb" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 220px auto" }, gap: 1.5, alignItems: "center" }}>
                  <TextField
                    placeholder="Search by name"
                    value={searchName}
                    onChange={(e) => {
                      setSearchName(e.target.value);
                      setPage(1);
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end"><SearchIcon style={{ color: "#fb7185" }} /></InputAdornment> }}
                  />
                  <TextField
                    placeholder="Search by Login Id"
                    value={searchLoginId}
                    onChange={(e) => {
                      setSearchLoginId(e.target.value);
                      setPage(1);
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end"><SearchIcon style={{ color: "#fb7185" }} /></InputAdornment> }}
                  />
                  <TextField
                    placeholder="Search by phone"
                    value={searchPhone}
                    onChange={(e) => {
                      setSearchPhone(e.target.value);
                      setPage(1);
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end"><SearchIcon style={{ color: "#fb7185" }} /></InputAdornment> }}
                  />
                  <FormControl size="small">
                    <Select
                      value={roleFilter}
                      onChange={(e) => {
                        setRoleFilter(e.target.value);
                        setPage(1);
                      }}
                    >
                      <MenuItem value="all">All Roles</MenuItem>
                      {ORG_ROLES.map((role) => (
                        <MenuItem key={role} value={role}>
                          {getRoleDisplayName(role)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {creatableOrgRoles.length > 0 && (
                      <IconButton ref={createButtonRef} onClick={openCreate} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
                        <AddIcon />
                      </IconButton>
                    )}
                    <IconButton onClick={() => refetch()} sx={{ color: "#334155" }}>
                      <RefreshIcon />
                    </IconButton>
                  </Stack>
                </Box>
              </Box>

              <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 88 }}>
                    <Select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))} MenuProps={ROWS_PER_PAGE_MENU_PROPS}>
                      {CUSTOM_ROWS_PER_PAGE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>
                          {getRowsPerPageLabel(size)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                    Showing {displayRange.from}-{displayRange.to} of {filteredUsers.length}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" onClick={resetFilters}>
                    Reset
                  </Button>
                  <Button size="small" onClick={handleDownloadCsv}>
                    Export CSV
                  </Button>
                  {isFetching ? <Chip size="small" label="Refreshing..." color="info" variant="outlined" /> : null}
                </Stack>
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "56vh", borderRadius: 0 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {["Name", "Role", "Password", "User ID", "Mobile", "Created By", "Status", "Last Seen", "Last Login", "Last Activity", "Disabled", "Actions"].map((head) => (
                        <TableCell key={head} sx={{ fontWeight: 700, color: "#4b5563", bgcolor: "#fff", borderBottom: "1px solid #d1d5db", whiteSpace: "nowrap" }}>
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedUsers.length ? (
                      pagedUsers.map((user) => (
                        <TableRow key={user._id} hover>
                          <TableCell sx={{ fontWeight: 700 }}>{user.username || "-"}</TableCell>
                          <TableCell>
                            <Chip size="small" label={user.roleLabel} sx={{ fontWeight: 700 }} />
                          </TableCell>
                          <TableCell>{renderPasswordCell(user)}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{user.uniqueId}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{user.mobileNo}</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{user.createdByName}</TableCell>
                          <TableCell sx={{ fontWeight: 800, color: user.isOnline ? "#16a34a" : "#6b7280", whiteSpace: "nowrap" }}>
                            {formatPresenceStatus(user.isOnline)}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{user.lastSeenLabel}</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{user.lastLoginLabel}</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{user.lastActivityLabel}</TableCell>
                          <TableCell>
                            <Switch
                              size="small"
                              checked={!!user.disabled}
                              disabled={!userActions.canEdit || togglingUserId === String(user._id) || String(user._id) === String(currentUser?._id)}
                              onChange={() => handleToggleDisabled(user)}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.6}>
                              {userActions.canEdit && (
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditingUser(user);
                                      setModalMode("edit");
                                    }}
                                    sx={{ bgcolor: "#2563eb", borderRadius: 1, color: "#fff" }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {userActions.canResetPassword && (
                                <Tooltip title="Reset Password">
                                  <IconButton
                                    size="small"
                                    onClick={() => setResetDialogUser(user)}
                                    sx={{ bgcolor: "#facc15", borderRadius: 1, color: "#111827" }}
                                  >
                                    <KeyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {userActions.canLoginAsUser && (
                                <Tooltip title="Login as User">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleLoginAsUser(user._id)}
                                    sx={{ bgcolor: "#ecfeff", borderRadius: 1, color: "#0891b2" }}
                                  >
                                    <LoginIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {user.isDeleted
                                ? userActions.canRestore && (
                                    <Tooltip title="Restore">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleRestoreUser(user._id)}
                                        sx={{ bgcolor: "#22c55e", borderRadius: 1, color: "#fff" }}
                                      >
                                        <RestoreFromTrashOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )
                                : userActions.canDelete && (
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteUser(user._id)}
                                        sx={{ bgcolor: "#ef4444", borderRadius: 1, color: "#fff" }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={12} align="center" sx={{ py: 6, color: "#64748b" }}>
                          No organization staff found for the selected filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                  Visible roles: {ORG_ROLES.map((role) => getRoleDisplayName(role)).join(", ")}
                </Typography>
                <Pagination
                  page={page}
                  count={Math.max(totalPages, 1)}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            </>
        </DialogContent>
      </Dialog>

      {modalMode !== "table" && (
        <CreateUserModal
          setShowCreateUserModal={closeForm}
          currentUserRole={currentUserRole}
          currentUser={currentUser}
          mode={modalMode === "edit" ? "edit" : "create"}
          initialData={editingUser}
          allowedRoles={creatableOrgRoles}
          hiddenPermissionKeys={ORG_HIDDEN_PERMISSION_KEYS}
          onSuccess={() => {
            refetch();
            closeForm();
            showStatus("success", modalMode === "edit" ? "Organization staff updated." : "Organization staff created.");
          }}
        />
      )}

      <Dialog open={Boolean(resetDialogUser)} onClose={() => setResetDialogUser(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Reset Password</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography sx={{ fontSize: 14, color: "#475569" }}>
              Set a new password for <b>{resetDialogUser?.username || "-"}</b>.
            </Typography>
            <TextField
              label="New Password"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      aria-label={showNewPassword ? "Hide password" : "Show password"}
                    >
                      {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResetDialogUser(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={isResettingPassword}>
            {isResettingPassword ? "Saving..." : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
